import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { FileText, Plus, Loader2, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PolicyType = 'usage_limit' | 'content_filter' | 'model_access' | 'cost_cap';
type EnforcementLevel = 'block' | 'warn' | 'log';
type PolicyScope = 'global' | 'team' | 'project';
type PolicyStatus = 'active' | 'draft';

interface Policy {
  id: string;
  name: string;
  description: string;
  policy_type: PolicyType;
  enforcement: EnforcementLevel;
  scope: PolicyScope;
  status: PolicyStatus;
  enabled: boolean;
  violations_count: number;
  updated_at: string;
}

interface PoliciesResponse {
  policies: Policy[];
  stats: {
    total: number;
    active: number;
    draft: number;
    violations: number;
  };
}

interface CreatePolicyPayload {
  name: string;
  description: string;
  policy_type: PolicyType;
  enforcement: EnforcementLevel;
  scope: PolicyScope;
  enabled: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function typeBadge(type: PolicyType) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  const labels: Record<PolicyType, string> = {
    usage_limit: 'Usage Limit',
    content_filter: 'Content Filter',
    model_access: 'Model Access',
    cost_cap: 'Cost Cap',
  };
  const colors: Record<PolicyType, string> = {
    usage_limit: 'bg-blue-500/15 text-blue-500',
    content_filter: 'bg-purple-500/15 text-purple-500',
    model_access: 'bg-emerald-500/15 text-emerald-500',
    cost_cap: 'bg-orange-500/15 text-orange-500',
  };
  return <span className={`${base} ${colors[type]}`}>{labels[type]}</span>;
}

function scopeBadge(scope: PolicyScope) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  const colors: Record<PolicyScope, string> = {
    global: 'bg-indigo-500/15 text-indigo-500',
    team: 'bg-cyan-500/15 text-cyan-500',
    project: 'bg-teal-500/15 text-teal-500',
  };
  return <span className={`${base} ${colors[scope]}`}>{scope}</span>;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function PoliciesPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreatePolicyPayload>({
    name: '',
    description: '',
    policy_type: 'usage_limit',
    enforcement: 'warn',
    scope: 'global',
    enabled: true,
  });

  const policiesQuery = useQuery<PoliciesResponse>({
    queryKey: ['governance', 'policies'],
    queryFn: () => api<PoliciesResponse>('governance/policies'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreatePolicyPayload) =>
      api('governance/policies', { body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'policies'] });
      setShowForm(false);
      setForm({ name: '', description: '', policy_type: 'usage_limit', enforcement: 'warn', scope: 'global', enabled: true });
    },
  });

  const stats = policiesQuery.data?.stats;
  const policies = policiesQuery.data?.policies ?? [];

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    createMutation.mutate(form);
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">AI Policies</h1>
            <p className="text-muted-foreground mt-1">
              Define and enforce usage policies across your AI systems.
            </p>
          </div>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 flex-shrink-0"
          >
            <Plus className="h-4 w-4" />
            Create Policy
          </button>
        )}
      </div>

      {/* Stats */}
      {policiesQuery.isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Total Policies</p>
            <p className="text-2xl font-bold text-foreground">{stats?.total ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-green-500">{stats?.active ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Draft</p>
            <p className="text-2xl font-bold text-yellow-500">{stats?.draft ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Violations</p>
            <p className="text-2xl font-bold text-red-500">{stats?.violations ?? 0}</p>
          </div>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">New Policy</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Daily Token Limit"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Policy Type</label>
              <select
                value={form.policy_type}
                onChange={(e) => setForm({ ...form, policy_type: e.target.value as PolicyType })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="usage_limit">Usage Limit</option>
                <option value="content_filter">Content Filter</option>
                <option value="model_access">Model Access</option>
                <option value="cost_cap">Cost Cap</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Describe what this policy enforces..."
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-y"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Enforcement</label>
              <select
                value={form.enforcement}
                onChange={(e) => setForm({ ...form, enforcement: e.target.value as EnforcementLevel })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="block">Block</option>
                <option value="warn">Warn</option>
                <option value="log">Log</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Scope</label>
              <select
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value as PolicyScope })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="global">Global</option>
                <option value="team">Team</option>
                <option value="project">Project</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-foreground pb-2">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                Enabled on creation
              </label>
            </div>
          </div>

          {createMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create policy.'}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={createMutation.isPending || !form.name.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {createMutation.isPending ? 'Creating...' : 'Create Policy'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Policy table */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">All Policies</h2>

        {policiesQuery.isLoading && (
          <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading policies...</span>
          </div>
        )}

        {policiesQuery.isSuccess && policies.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
            No policies yet. Create your first policy to start governing AI usage.
          </div>
        )}

        {policiesQuery.isSuccess && policies.length > 0 && (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <span className="flex-1">Name</span>
              <span className="w-28">Type</span>
              <span className="w-20">Scope</span>
              <span className="w-16">Status</span>
              <span className="w-24">Enforcement</span>
              <span className="w-28">Last Updated</span>
            </div>
            {policies.map((policy) => (
              <div
                key={policy.id}
                className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-accent/50"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{policy.name}</p>
                  {policy.description && (
                    <p className="text-xs text-muted-foreground truncate">{policy.description}</p>
                  )}
                </div>
                <span className="w-28">{typeBadge(policy.policy_type)}</span>
                <span className="w-20">{scopeBadge(policy.scope)}</span>
                <span className="w-16">
                  {policy.status === 'active' ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/15 text-green-500">Active</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/15 text-yellow-500">Draft</span>
                  )}
                </span>
                <span className="w-24 text-sm text-muted-foreground capitalize">{policy.enforcement}</span>
                <span className="w-28 text-sm text-muted-foreground">{formatDate(policy.updated_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
